// V2 版式：perceptual-map（前端 DOM 版）——对照后端 v2/layouts/perceptual-map.ts 逐坐标移植
// 感知图：十字轴 + 竞品/self 散点 + gap 虚线椭圆；渲染前跑同款防重叠位移。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { isLuxe } from "./luxe";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2PerceptualMap({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const GEO = V2GEO["perceptual-map"] as any;
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // luxe：散点同杂志用圆点（perceptual-map.ts mag||luxe 分支）
  const luxe = isLuxe(skin);
  const theme = skin.theme as { grey?: { grey2?: string; grey3?: string } };
  const grey2 = theme.grey?.grey2 ?? skin.hairline;
  const grey3 = theme.grey?.grey3 ?? skin.sub;

  const bi = c.blocks.findIndex((x) => x.type === "scatter");
  const b = bi >= 0 ? c.blocks[bi] : null;
  if (!b || b.type !== "scatter") {
    return <V2Chrome skin={skin} kicker={kickerOf(page, "perceptual-map")} page={page} total={total} foot={footOf()} />;
  }

  const cx = GEO.plotX + GEO.plotW / 2;
  const cy = GEO.plotY + GEO.plotH / 2;
  const mapXY = (x: number, y: number) => ({
    x: GEO.plotX + ((x + 1) / 2) * GEO.plotW,
    y: GEO.plotY + ((1 - y) / 2) * GEO.plotH,
  });
  // 防重叠位移（与后端 spreadPoints 同款）
  const pts = b.points.map((p) => ({ ...p, ...mapXY(p.x, p.y) }));
  for (let round = 0; round < 6; round++) {
    let moved = false;
    for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
      const dx = pts[j].x - pts[i].x, dy = pts[j].y - pts[i].y;
      const d = Math.hypot(dx, dy);
      if (d < GEO.minDist) {
        moved = true;
        const ang = d > 0.001 ? Math.atan2(dy, dx) : ((i + j) % 4) * Math.PI / 2;
        const push = (GEO.minDist - d) / 2;
        pts[i].x -= Math.cos(ang) * push; pts[i].y -= Math.sin(ang) * push;
        pts[j].x += Math.cos(ang) * push; pts[j].y += Math.sin(ang) * push;
      }
    }
    if (!moved) break;
  }
  for (const p of pts) {
    p.x = Math.min(Math.max(p.x, GEO.plotX + 0.3), GEO.plotX + GEO.plotW - 0.3);
    p.y = Math.min(Math.max(p.y, GEO.plotY + 0.3), GEO.plotY + GEO.plotH - 0.3);
  }
  const gap = mapXY(b.gap.x, b.gap.y);
  const axisStyle = { fontSize: pt(skin.scale.meta), color: skin.sub, fontFamily: skin.fontBody } as const;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "perceptual-map")} page={page} total={total} foot={footOf()} />
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(W - 2 * m), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {/* 十字轴 */}
      <div style={{ position: "absolute", left: px(GEO.plotX), top: px(cy), width: px(GEO.plotW), height: 1, background: skin.sub }} />
      <div style={{ position: "absolute", left: px(cx), top: px(GEO.plotY), width: 1, height: px(GEO.plotH), background: skin.sub }} />
      {/* 四端轴标签 */}
      <div style={{ position: "absolute", left: px(GEO.plotX - GEO.axisLblW), top: px(cy - GEO.axisLblH / 2), width: px(GEO.axisLblW - 0.08), height: px(GEO.axisLblH), textAlign: "right", ...axisStyle }}>{b.xNeg}</div>
      <div style={{ position: "absolute", left: px(GEO.plotX + GEO.plotW + 0.08), top: px(cy - GEO.axisLblH / 2), width: px(GEO.axisLblW - 0.08), height: px(GEO.axisLblH), ...axisStyle }}>{b.xPos}</div>
      <div style={{ position: "absolute", left: px(cx - GEO.axisLblW), top: px(GEO.plotY - GEO.axisLblH - 0.04), width: px(GEO.axisLblW * 2), height: px(GEO.axisLblH), textAlign: "center", ...axisStyle }}>{b.yPos}</div>
      <div style={{ position: "absolute", left: px(cx - GEO.axisLblW), top: px(GEO.plotY + GEO.plotH + 0.04), width: px(GEO.axisLblW * 2), height: px(GEO.axisLblH), textAlign: "center", ...axisStyle }}>{b.yNeg}</div>
      {/* gap 空白机会区 */}
      <div style={{
        position: "absolute", left: px(gap.x - GEO.gapW / 2), top: px(gap.y - GEO.gapH / 2),
        width: px(GEO.gapW), height: px(GEO.gapH), borderRadius: "50%",
        border: `1px dashed ${skin.accent}`,
      }} />
      <div style={{
        position: "absolute", left: px(gap.x - GEO.gapW / 2), top: px(gap.y + GEO.gapH / 2 + 0.02),
        width: px(GEO.gapW), height: px(0.3), textAlign: "center",
        fontSize: pt(skin.scale.caption), color: skin.accent, fontFamily: skin.fontBody, fontWeight: 700,
      }}>✦ {b.gap.label}</div>
      {/* 散点 */}
      {pts.map((p, i) => {
        const self = p.role === "self";
        const s = self ? GEO.pointSize * 1.5 : GEO.pointSize;
        return (
          <div key={i}>
            <div style={{
              position: "absolute", left: px(p.x - s / 2), top: px(p.y - s / 2), width: px(s), height: px(s),
              background: self ? skin.accent : grey2, borderRadius: mag || luxe ? "50%" : 0,
              border: self ? `1.5px solid ${skin.accent}` : undefined,
            }} />
            <div style={{
              position: "absolute", left: px(p.x - 0.85), top: px(p.y + s / 2 + 0.02), width: px(1.7), height: px(0.28),
              textAlign: "center", fontSize: pt(skin.scale.caption),
              color: self ? skin.accent : grey3, fontFamily: skin.fontBody, fontWeight: self ? 700 : 400,
            }}>{p.name}</div>
          </div>
        );
      })}
    </>
  );
}
